/* eslint-disable import/prefer-default-export */
import { useEffect } from 'react';
import CookiesProvider from '../providers/Cookies';

const CONSENT_LEVEL = {
  ESSENTIAL: 'Essential',
  FUNCTIONAL: 'Functional',
  MARKETING: 'Marketing',
  PERFORMANCE: 'Performance',
};

const getUcmCookieHelpers = (consentLevel, key, value, options = {}) => {
  const isLocalhost = window?.location?.hostname?.includes('localhost') === true;
  const setCookie = () => (isLocalhost
    ? CookiesProvider.set(key, value, options)
    : window.ResyUCM?.[`set${consentLevel}Cookie`](key, value, options));
  const isScriptLoaded = `ResyUCM${consentLevel}CookieScript_isLoaded`;
  const scriptLoadedEvent = `ResyUCM${consentLevel}CookieScript_loaded`;

  return {
    isLocalhost,
    setCookie,
    isScriptLoaded,
    scriptLoadedEvent,
  };
};

const getCookieSetter = (consentLevel) => (key, value, options = {}) => {
  const {
    isLocalhost,
    setCookie,
    isScriptLoaded,
    scriptLoadedEvent,
  } = getUcmCookieHelpers(consentLevel, key, value, options);

  if (isLocalhost) {
    setCookie();
    return () => {
      CookiesProvider.remove(key, { path: options?.path || '/' });
    };
  }

  if (window[isScriptLoaded]) {
    setCookie();
  } else {
    window.addEventListener(scriptLoadedEvent, setCookie);
  }

  // eslint-disable-next-line consistent-return
  return () => window.removeEventListener(scriptLoadedEvent, setCookie);
};

const getCookieSetterAsync = (consentLevel) => (key, value, options = {}) => new Promise((resolve) => {
  const {
    isLocalhost,
    setCookie,
    isScriptLoaded,
    scriptLoadedEvent,
  } = getUcmCookieHelpers(consentLevel, key, value, options);

  const setCookieAndResolve = () => {
    setCookie();
    resolve();
  };

  if (isLocalhost) {
    setCookieAndResolve();
    return;
  }

  if (window[isScriptLoaded]) {
    setCookieAndResolve();
  } else {
    const onLoaded = () => {
      window.removeEventListener(scriptLoadedEvent, onLoaded);
      setCookieAndResolve();
    };
    window.addEventListener(scriptLoadedEvent, onLoaded);
  }
});

const setEssentialCookie = getCookieSetter(CONSENT_LEVEL.ESSENTIAL);

const setEssentialCookieAsync = getCookieSetterAsync(CONSENT_LEVEL.ESSENTIAL);

const useEssentialCookie = ((key, value, options) => {
  useEffect(() => setEssentialCookie(key, value, options), []);
});

const setFunctionalCookie = getCookieSetter(CONSENT_LEVEL.FUNCTIONAL);

const useFunctionalCookie = ((key, value, options) => {
  useEffect(() => setFunctionalCookie(key, value, options), []);
});

const setMarketingCookie = getCookieSetter(CONSENT_LEVEL.MARKETING);

const useMarketingCookie = ((key, value, options) => {
  useEffect(() => setMarketingCookie(key, value, options), []);
});

const setPerformanceCookie = getCookieSetter(CONSENT_LEVEL.PERFORMANCE);

const usePerformanceCookie = ((key, value, options) => {
  useEffect(() => setPerformanceCookie(key, value, options), []);
});

export {
  setEssentialCookie,
  setEssentialCookieAsync,
  setFunctionalCookie,
  setMarketingCookie,
  setPerformanceCookie,
  useEssentialCookie,
  useFunctionalCookie,
  useMarketingCookie,
  usePerformanceCookie,
};
